import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, CheckCircle2, Link2, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { usePageTitle } from '@/hooks/use-page-title'
import { API_URL } from '@/config'

const InitKartickaSchema = z.object({
	branchId: z.string().min(1, 'ID pobočky je povinné').regex(/^\d+$/, 'Musí být číslo'),
})

type InitKartickaFormData = z.infer<typeof InitKartickaSchema>

type BranchJoinResponse =
	| { success: true; redirectUrl: string }
	| { success: false; errmsg: string }

async function joinBranch(input: { branchId: number; backlink: string }): Promise<BranchJoinResponse> {
	const res = await fetch(`${API_URL}/karticka/branch/join`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(input),
	})
	return res.json()
}

export function InitKartickaPage() {
	usePageTitle('Připojení pobočky')
	const [searchParams] = useSearchParams()
	const backlink = searchParams.get('backlink')
	const [success, setSuccess] = useState(false)

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<InitKartickaFormData>({
		// zodResolver's types brand against zod v4 core minor: 0 while our zod
		// ships with minor: 3; runtime is fine, cast silences the mismatch.
		resolver: zodResolver(InitKartickaSchema as never),
	})

	const joinMutation = useMutation({
		mutationFn: joinBranch,
		onSuccess: (result) => {
			if (!result.success) return
			setSuccess(true)
			setTimeout(() => {
				window.location.href = result.redirectUrl
			}, 1000)
		},
	})

	const errorMessage = joinMutation.isError
		? 'Nastala chyba. Zkuste to prosím znovu.'
		: joinMutation.data && !joinMutation.data.success
			? joinMutation.data.errmsg || 'Připojení se nezdařilo'
			: null

	const onSubmit = (data: InitKartickaFormData) => {
		if (!backlink) return
		joinMutation.mutate({ branchId: Number(data.branchId), backlink })
	}

	return (
		<div className="min-h-screen flex flex-col bg-white">
			{/* Top bar */}
			<div className="border-b border-gray-200 bg-white">
				<div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
					<img src="/agnis_logo_crop.png" alt="Agnis" className="h-8" />
					<div className="flex items-center gap-2 text-xs text-gray-400">
						<ShieldCheck className="h-3.5 w-3.5" />
						<span>Zabezpečené připojení</span>
					</div>
				</div>
			</div>

			{/* Main content */}
			<div className="flex-1 flex items-center justify-center px-4 py-12">
				<div className="w-full max-w-sm">
					{success ? (
						<div className="text-center space-y-4 animate-scale-fade">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50">
								<CheckCircle2 className="h-8 w-8 text-green-600" />
							</div>
							<div>
								<h1 className="text-xl font-semibold text-gray-900">Připojeno</h1>
								<p className="text-sm text-gray-500 mt-1">
									Přesměrování zpět...
								</p>
							</div>
						</div>
					) : (
						<>
							{/* Icon + heading */}
							<div className="text-center mb-8">
								<div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 mb-4">
									<Link2 className="h-7 w-7 text-red-600" />
								</div>
								<h1 className="text-2xl font-bold tracking-tight text-gray-900">Připojení pobočky</h1>
								<p className="text-sm text-gray-500 mt-2">
									Propojte svou pobočku se systémem Agnis
								</p>
							</div>

							{!backlink ? (
								<div className="rounded-xl border border-red-200 bg-red-50 p-4">
									<p className="text-red-700 text-sm text-center leading-relaxed">
										Tato stránka musí být otevřena z externího systému.
										Chybí parametr pro zpětné přesměrování.
									</p>
								</div>
							) : (
								<div className="space-y-6">
									<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
										<div className="space-y-2">
											<label htmlFor="branchId" className="text-sm font-medium text-gray-700">
												ID pobočky
											</label>
											<Input
												id="branchId"
												inputMode="numeric"
												autoFocus
												placeholder="např. 1042"
												className="h-12 text-center text-lg tracking-widest font-mono text-gray-900 bg-white border-gray-300 focus-visible:ring-red-500"
												{...register('branchId')}
											/>
											{errors.branchId && (
												<p className="text-red-600 text-sm text-center">
													{errors.branchId.message}
												</p>
											)}
										</div>

										{errorMessage && (
											<div className="rounded-lg bg-red-50 border border-red-200 p-3">
												<p className="text-red-700 text-sm text-center">{errorMessage}</p>
											</div>
										)}

										<Button type="submit" className="w-full h-11" disabled={joinMutation.isPending}>
											{joinMutation.isPending ? (
												<>
													<LoadingSpinner size="sm" className="mr-2" />
													Připojování...
												</>
											) : (
												<>
													Připojit
													<ArrowRight className="ml-2 h-4 w-4" />
												</>
											)}
										</Button>
									</form>

									{/* Steps info */}
									<div className="border-t border-gray-100 pt-5">
										<div className="space-y-3">
											{[
												{ step: '1', text: 'Zadejte ID pobočky z vašeho systému' },
												{ step: '2', text: 'Ověříme a propojíme vaši pobočku' },
												{ step: '3', text: 'Budete přesměrováni zpět s přístupovými údaji' },
											].map((item) => (
												<div key={item.step} className="flex items-start gap-3">
													<span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-400 text-xs font-medium flex items-center justify-center">
														{item.step}
													</span>
													<p className="text-sm text-gray-400 leading-5">{item.text}</p>
												</div>
											))}
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	)
}
